import { Injectable } from '@angular/core';
import { Subject } from 'rxjs/Subject';
import { Observable } from 'rxjs/Observable';
import { Router, NavigationEnd } from '@angular/router';
import { ROUTES } from '../../../../../../clpbp-app.settings';
import { MortgageSessionService } from '../../../mortgage/mortgage-session/mortgage-session.service';
import { QueryParamService } from '../query-param-service/query-param.service';
import { PersonalLoanSessionService } from '../../../personal-loans/personal-loans-session/personal-loans-session.service';
import { SessionDataModel } from '../models/session-data.model';
import { LoanAppTask } from '@shared/models';
import { SessionApi } from '../../../../../../../../generated/internalApi/index';

export enum ApplicationTypeEnum {
  Mortgage = <any> 'Mortgage',
  PersonalLoans = <any> 'PersonalLoans',
  Global = <any> 'Global',
  Dashboard = <any> 'Dashboard',
  Login = <any> 'Login'
}

@Injectable()
export class SessionService {

  public isCounterOffer: boolean;
  // This is to fix the PL name showing in header no matter what for shared email because borrowerService.getUnifiedBorrower checks
  // for existence of PL Guid and then gets PL user before checking for existence of Mortgage Guid so in the case that both exist
  // PL overrides Mortgage
  public useMortgageBorrowerGuid: boolean;

  public mortgageSession: MortgageSessionService;
  public personalLoanSession: PersonalLoanSessionService;

  // this is for authService to know about activity. each call resets a timer.
  public isActive: Subject<SessionDataModel> = new Subject<SessionDataModel>();

  // @TODO currently overridden in HeaderComponent, needs refactoring
  public logout: () => void;

  // To send out 'application type' by Subject
  // if you need to determine application type in your component, subscribe to the getApplicationType() in your code
  private _applicationTypeNotify: Subject<ApplicationTypeEnum> = new Subject<ApplicationTypeEnum>();

  // Data that is saved to session storage
  public set data(value: SessionDataModel) {
    this._data = value;
    this._data.setSessionData(value);
  }
  public get data(): SessionDataModel {
    if (this.isActive.observers) {
      this.isActive.next(this._data);
    }
    return this._data;
  }

  public _mismatchedSSN: boolean;

  public get isMismatchedSSN(): boolean {
    return this._mismatchedSSN;
  }

  public _mismatchSSNCount: number = 0;

  public get misMatchedSSNCount(): number {
    return this._mismatchSSNCount;
  }

  // TODO: Migrate to Personal Loans specific session state
  // I'm not sure why all this was put here or how it passed CR, but
  // PL-specific stuff should go in PL session. Why is this at the top level
  // if it's only tracking state for PL tasks prior to submitting app?
  public _tasksToBeSubmitted: LoanAppTask[] = [];

  public get tasksToBeSubmitted(): LoanAppTask[] {
    return this._tasksToBeSubmitted;
  }

  public addTaskToSubmit(value: LoanAppTask): void {
    this._tasksToBeSubmitted.push(value);
  }

  public resetTasksToSubmit(): void {
    this._tasksToBeSubmitted = [];
  }

  // query parameter values that are strings must already be encoded by encodeURIComponent
  public navigateToUla(path: string,
    queryParams?: { [key: string]: boolean | number | string }): Promise<boolean> {
    // CLPBP-34 log out here and redirect to ULA login
    path = path[0] !== '/' ? '/' + path : path;
    if (queryParams) {
      path = (path.indexOf('?') > -1 ? path + '&' : path + '?') +
        Object.entries(queryParams).map(([key, value]) => `${key}=${value}`).join('&');
    }
    return new Promise<boolean>((resolve, reject) => {
      this.sessionApi.sessionGetRedirectUrl(this._data.authData.token)
      .subscribe(response => {
        // success and url is not null
        if (response && response.success && typeof response.data === 'string' && response.data.length > 0) {
          window.location.replace(response.data + path);
          resolve(true);
        } else if (response && response.success) {
          // success and url is null - do not redirect
          resolve(false);
        } else {
          // error - nav to 404
          this.router.navigate(['/404']);
          console.error('Url failed to retrieve');
        }
      },
      err => {
        console.error('Unable to get redirect url from public api', err);
        // error - nav to 404
        this.router.navigate(['/404']);
        reject(err);
      });
    });
  }

  constructor(
    private router: Router,
    private mortgageSessionService: MortgageSessionService,
    private personalLoanSessionService: PersonalLoanSessionService,
    private _data: SessionDataModel,
    private sessionApi: SessionApi,
    public params: QueryParamService) {

    this.mortgageSession = mortgageSessionService;
    this.personalLoanSession = personalLoanSessionService;

    const existingSession = this.data.getSessionData();
    if (!existingSession) {
      this.data = _data;
    } else {
      this.data.populate(existingSession);
    }

    this.router.events.subscribe(data => {
      if (data instanceof NavigationEnd) {
        this.init(this.router.url);
      }
    });
  }

  public mismatchedSSN(value: boolean): void {
    this._mismatchedSSN = value;
  }

  public increaseMismatchSSNCount(): void {
    this._mismatchSSNCount++;
  }

  public resetMisMatchSSNCount(): void {
    this._mismatchSSNCount = 0;
  }

  private determineApplicationType(url: string, shouldNotify: boolean = true): void {

    const tryNotify = (type: ApplicationTypeEnum) => {
      if (shouldNotify) {
        this.notifyApplicationTypeChanged(type);
      }
    };

    if (url.indexOf(`/${ ROUTES.MORTGAGE_FULL }`) > -1) {
      tryNotify(ApplicationTypeEnum.Mortgage);
    } else if (url.indexOf(`/${ ROUTES.PL_FULL }`) > -1) {
      tryNotify(ApplicationTypeEnum.PersonalLoans);
    } else if (url.indexOf(`/${ ROUTES.DASHBOARD_FULL }`) > -1) {
      tryNotify(ApplicationTypeEnum.Dashboard);
    }  else if (
      url.indexOf(`${ ROUTES.LOGIN_FULL }`) > -1
      || url.indexOf(`${ ROUTES.SIGNIN_FULL }`) > -1
    ) {
      tryNotify(ApplicationTypeEnum.Login);
    } else if (url.indexOf(`${ ROUTES.APP_BASE }`) > -1) {
      tryNotify(ApplicationTypeEnum.Global);
    } else {
      console.error('We have an app flow which is neither mortgage nor personal loans. ' +
        'Update type-sensitive session & stepper logic!');
    }
  }

  protected init(url: string): void {
    this.determineApplicationType(url);
  }

  // You are considered authenticated if you hold a token.  This assumes that expired tokens are cleared out.
  public get authenticated(): boolean {
    if (this.data && this.data.authData.token) {
      return !!this.data.authData.token;
    }

    return false;
  }

  public get borrowerGuid(): string {
    return this.personalLoanSession.data.borrowerData.borrowerGuid;
  }

  public notifyApplicationTypeChanged(type: ApplicationTypeEnum) {
    this._applicationTypeNotify.next(type);
  }

  public onApplicationTypeChange(): Observable<ApplicationTypeEnum> {
    return this._applicationTypeNotify.asObservable();
  }

  public resetLoanApplication(): void {
    this.data.clear();
    this.mortgageSession.resetLoanApplication();
    this.personalLoanSession.resetLoanApplication();
  }
}
